/**
 * Testing Store
 *
 * Holds candidate data for values testing comparison.
 * This store is populated when navigating from the Proposal page to the Values Testing page.
 * The "actual" values live here; "expected" (baseline) values come from the API.
 */

import type { CandidateSummary } from '@shared/models';
import { create } from 'zustand';
import type { Candidate, CashFlow, OptimizationHistoryEntry } from '~/types/candidate';

// Re-export for convenience
export type { CandidateSummary } from '@shared/models';

/**
 * Extract the base shape ID for comparison.
 * Note: The shapeId in millageInformation.millageShape is preserved as the base shape
 * even after optimizations (candidate.name uses {shapeId}.{count} format, but
 * millageShape.shapeId remains the raw shape identifier).
 */
function getBaseShapeId(fullShapeId: string): string {
  return fullShapeId;
}

/**
 * Convert a full Candidate to a CandidateSummary for comparison
 *
 * Note: Uses base shapeId (without optimizer suffixes) as the name for clean identification.
 * Optimization history is extracted to show which optimizations were applied.
 */
export function candidateToSummary(candidate: Candidate): CandidateSummary {
  const fullShapeId = candidate.millageInformation.millageShape.shapeId;
  const shapeId = getBaseShapeId(fullShapeId);

  // Extract applied optimizations (filter out initial-shape and optimization-skipped)
  const appliedOptimizations =
    candidate.optimizationHistory
      ?.filter((h) => h.type !== 'initial-shape' && h.type !== 'optimization-skipped')
      .map((h) => h.type) ?? [];

  return {
    name: shapeId,
    shapeId: shapeId,
    viable: candidate.results.isViable,
    totalRevenue: candidate.results.totalRevenue,
    totalPrincipal: candidate.results.totalPrincipal,
    totalInterest: candidate.results.totalInterest,
    revenueUtilization: candidate.results.revenueUtilization,
    efficiency: candidate.results.efficiency,
    appliedOptimizations,
  };
}

/**
 * Candidate with cashflows and optimization history for detailed comparison
 */
export interface CandidateWithCashflows {
  name: string;
  shapeId: string;
  cashFlows: CashFlow[];
  /** Optimization history for display (original shape -> final state) */
  optimizationHistory?: OptimizationHistoryEntry[];
}

/**
 * Data loaded for testing comparison
 */
export interface TestingData {
  projectId: string;
  projectName: string;
  configurationId: string;
  configurationName: string;
  // Current calculated values (the "actual" data)
  candidateSummaries: CandidateSummary[];
  /** All candidates with their cashflows (for name-based lookup) */
  candidatesWithCashflows: CandidateWithCashflows[];
  /** @deprecated Use candidatesWithCashflows with name lookup instead */
  topCandidateCashFlows: CashFlow[];
  // Metadata
  loadedAt: string;
}

export interface TestingStoreState {
  // Current testing data (null if not loaded)
  testingData: TestingData | null;

  // Actions
  loadTestingData: (
    projectId: string,
    projectName: string,
    configurationId: string,
    configurationName: string,
    candidates: Candidate[]
  ) => void;
  clearTestingData: () => void;
}

export const useTestingStore = create<TestingStoreState>()((set) => ({
  testingData: null,

  loadTestingData: (projectId, projectName, configurationId, configurationName, candidates) => {
    // Convert candidates to summaries for comparison
    const candidateSummaries = candidates.map(candidateToSummary);

    // Store all candidates with their cashflows and optimization history for shapeId-based lookup
    // Use base shapeId (without _custom-N suffixes) for consistency with candidateSummaries
    const candidatesWithCashflows: CandidateWithCashflows[] = candidates.map((c) => {
      const shapeId = getBaseShapeId(c.millageInformation.millageShape.shapeId);
      return {
        name: shapeId,
        shapeId: shapeId,
        cashFlows: c.cashFlows,
        optimizationHistory: c.optimizationHistory,
      };
    });

    // Get top candidate's cashflows (kept for backwards compatibility)
    const topCandidate = candidates[0];
    const topCandidateCashFlows = topCandidate?.cashFlows ?? [];

    set({
      testingData: {
        projectId,
        projectName,
        configurationId,
        configurationName,
        candidateSummaries,
        candidatesWithCashflows,
        topCandidateCashFlows,
        loadedAt: new Date().toISOString(),
      },
    });
  },

  clearTestingData: () => {
    set({ testingData: null });
  },
}));
